import { useLocation, useNavigate } from 'react-router-dom';
import { Compass, House } from 'lucide-react';
import Layout from '../components/Layout';

export default function NotFound() {
  const location = useLocation();
  const navigate = useNavigate();

  const goHome = () => {
    navigate('/');
    window.scrollTo({ top: 0 });
  };

  const goIntegrations = () => {
    navigate('/integrations');
    window.scrollTo({ top: 0 });
  };

  return (
    <Layout>
      <div className="relative min-h-screen w-full bg-[#0A0F0A] flex items-center justify-center px-6 overflow-hidden">
        {/* Background ambience - radial glow + dot grid */}
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute inset-0"
            style={{
              background:
                'radial-gradient(ellipse 60% 50% at 50% 50%, rgba(0,229,153,0.08) 0%, transparent 70%)',
            }}
          />
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                'radial-gradient(circle, rgba(224,238,238,0.05) 1px, transparent 1px)',
              backgroundSize: '24px 24px',
              maskImage:
                'radial-gradient(ellipse 70% 60% at 50% 50%, black 30%, transparent 90%)',
              WebkitMaskImage:
                'radial-gradient(ellipse 70% 60% at 50% 50%, black 30%, transparent 90%)',
            }}
          />
        </div>

        <div className="relative z-10 max-w-2xl mx-auto text-center pt-16">
          {/* Giant 404 with vertical fade */}
          <h1
            className="text-[8rem] sm:text-[10rem] md:text-[14rem] font-black leading-none mb-2 select-none tracking-tighter"
            style={{
              backgroundImage:
                'linear-gradient(180deg, #E0EEEE 0%, #00E599 50%, transparent 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              color: 'transparent',
            }}
          >
            404
          </h1>

          {/* Status badge */}
          <div className="mb-6 -mt-4 sm:-mt-6">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-red-500/10 border border-red-500/25 rounded-full text-[11px] font-bold text-red-400 uppercase tracking-wider">
              <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
              Page Declined
            </span>
          </div>

          <h2 className="text-2xl md:text-3xl font-bold text-[#E0EEEE] mb-4">
            This route couldn't be authorised.
          </h2>

          <p className="text-base text-[#E0EEEE]/60 mb-2 leading-relaxed">
            We couldn't find the page at{' '}
            <code className="text-[#00E599] font-mono text-sm bg-[#001A1A]/60 border border-[#00E599]/15 px-2 py-0.5 rounded">
              {location.pathname}
            </code>
          </p>
          <p className="text-sm text-[#E0EEEE]/40 mb-10 italic">
            Unlike failed payments, this one isn't recoverable.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={goHome}
              className="group inline-flex items-center justify-center gap-2 bg-[#00E599] hover:bg-[#00D48A] text-[#0A0F0A] px-6 py-3 rounded-lg font-bold text-sm transition-all duration-300 hover:scale-[1.03] hover:shadow-xl hover:shadow-[#00E599]/20 cursor-pointer btn-shine btn-press"
            >
              <House className="w-4 h-4" />
              Back to Home
            </button>
            <button
              onClick={goIntegrations}
              className="group inline-flex items-center justify-center gap-2 bg-white/[0.04] hover:bg-white/[0.08] text-white px-6 py-3 rounded-lg font-bold text-sm border border-white/[0.12] hover:border-white/25 transition-all duration-300 hover:scale-[1.03] backdrop-blur-sm cursor-pointer btn-press"
            >
              <Compass className="w-4 h-4" />
              Explore Integrations
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
